import { useSelector } from "react-redux";
import { Table, Button, Tag, Popconfirm, Badge, Avatar, Popover } from "antd";
import {
  MailOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseCircleOutlined,
  BulbOutlined,
  StopOutlined,
  HarmonyOSOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import { SelectedItemsControl } from "./SelectedItemsControl";
import { useDispatch } from "react-redux";
import {
  deletePlayer,
  sendWelcomeEmailToPlayer,
} from "../../../store/actions/team.actions";
import { useMemo, useEffect } from "react";
import "./PlayerTable.css";

export const DeletePopConfirmContent = () => {
  return (
    <div style={{ width: 256 }}>
      This is a permanent action. <br />
      You are about to delete this player's data and login credentials.
      <br />
      If you are unsure about this or just want to hide this player's data from
      the platform, set them to
      <Tag color="red" style={{ margin: "0px 4px" }}>
        Inactive
      </Tag>
      instead.
    </div>
  );
};

export const PlayerTable = ({ handleOpenEditModal, userPermissions }) => {
  const dispatch = useDispatch();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [sentEmails, setSentEmails] = useState({});
  const [allPlayers, setAllPlayers] = useState([])

  const { allPlayers: allPlayersRedux, allPositions } = useSelector(({ team }) => team);

  const canEditPlayer = userPermissions?.RosterEditPlayer == true ? true : false;
  const canDeletePlayer = userPermissions?.RosterDeletePlayer == true ? true : false;
  const canSendWelcomeEmail = userPermissions?.SendWelcomeEmail == true ? true : false;

  useEffect(() => {
    setAllPlayers(allPlayersRedux.sort((a, b) => b.player_id - a.player_id))
  }, [])

  const playersWithValidity = useMemo(() => {
    const names = allPlayers.reduce((acc, player) => {
      const name = `${player.first_name}-${player.last_name}`;
      if (!acc[name]) acc[name] = 0;
      acc[name]++;
      return acc;
    }, {});
    const emails = allPlayers.reduce((acc, player) => {
      const email = player.email;
      if (!acc[email]) acc[email] = 0;
      acc[email]++;
      return acc;
    }, {});
    return allPlayers.map((player) => {
      const name = `${player.first_name}-${player.last_name}`;
      if (
        names[name] > 1 ||
        (player.email && emails[player.email] > 1) ||
        !allPositions.includes(player.position) ||
        !player.first_name ||
        !player.last_name ||
        !player.position ||
        player.number === "" ||
        player.number === undefined
      ) {
        return { ...player, valid: false };
      } else {
        return { ...player, valid: true };
      }
    });
  }, [allPlayers, allPositions]);

  const selectedRows = useMemo(
    () =>
      playersWithValidity.filter(({ player_id }) =>
        selectedRowKeys.includes(player_id)
      ),
    [selectedRowKeys, playersWithValidity]
  );

  const validationStatus = useMemo(() => {
    return playersWithValidity.reduce(
      (acc, player) => {
        if (player.valid) acc.valid++;
        if (!player.valid) acc.error++;
        return acc;
      },
      { valid: 0, error: 0, warning: 0 }
    );
  }, [playersWithValidity]);

  const handleSendWelcomeEmail = (record) => async () => {
    const result = await dispatch(sendWelcomeEmailToPlayer(record.player_id));
    setSentEmails((sentEmails) => ({
      ...sentEmails,
      [record.player_id]: result ? "green" : "red",
    }));
  };

  const handleDeletePlayer = (record) => () => {
    dispatch(deletePlayer(record.player_id));
  };

  const columns = [
    {
      title: "First Name",
      dataIndex: "first_name",
      key: "first_name",
      sorter: (a, b) => {
        if (a.first_name === null) return -1;
        if (b.first_name === null) return 1;
        return a.first_name.localeCompare(b.first_name);
      },
    },
    {
      title: "Last Name",
      dataIndex: "last_name",
      key: "last_name",
      sorter: (a, b) => {
        if (a.last_name === null) return -1;
        if (b.last_name === null) return 1;
        return a.last_name.localeCompare(b.last_name);
      },
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
      filters: allPositions.map(position => ({ text: position, value: position })),
      onFilter: (value, record) => record["position"] === value,
      sorter: (a, b) => {
        if (a.position === null) return -1;
        if (b.position === null) return 1;
        return a.position.localeCompare(b.position);
      },
    },
    {
      title: "Jersey Number",
      dataIndex: "number",
      key: "number",
      sorter: (a, b) => {
        if (a.number === null) return -1;
        if (b.number === null) return 1;
        return a.number - b.number;
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => {
        if (a.email === null) return -1;
        if (b.email === null) return 1;
        return a.email.localeCompare(b.email);
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: 'Active', value: false },
        { text: 'Inactive', value: true },
      ],
      onFilter: (value, record) => record["retired"] === value,
      sorter: (a, b) => {
        return (a.retired === b.retired) ? 0 : a.retired ? 1 : -1;
      },
      width: 128,
      render: (_, record) => {
        return (
          <div
            style={{
              display: "flex",
              gap: 4,
              width: "100%",
              justifyContent: "center",
            }}
          >
            {record.integrations?.map(integ_name => {
              const string = integ_name[0].toUpperCase() + integ_name.slice(1) + " connected"
              return (
                <Popover content={string} overlayStyle={{lineHeight:'5px'}}>
                    <Avatar 
                      style={{ backgroundColor: 'transparent', color: '#464646'}} 
                      icon={<HarmonyOSOutlined style={{rotate: '180deg'}}/>} 
                      size="small"
                    />
                  </Popover>
                )
            }
            )}
            {record.retired ? (
              <Tag
                color="red"
                style={{ display: "flex", alignItems: "center" }}
              >
                Inactive
              </Tag>
            ) : (
              <Tag
                color="cyan"
                style={{ display: "flex", alignItems: "center" }}
              >
                Active
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Badge
            dot={!!sentEmails[record.player_id]}
            color={sentEmails[record.player_id]}
            offset={[-5, 5]}
          >
            <Button
              icon={<MailOutlined />}
              shape="circle"
              onClick={handleSendWelcomeEmail(record)}
              disabled={!record.email || !canSendWelcomeEmail}
            />
          </Badge>
          <Button
            icon={<EditOutlined />}
            shape="circle"
            type="primary"
            ghost
            onClick={handleOpenEditModal(record)}
            disabled={!canEditPlayer}
          />
          <Popconfirm
            placement="left"
            title={<DeletePopConfirmContent />}
            okText="Yes"
            cancelText="No"
            onConfirm={handleDeletePlayer(record)}
            icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
            disabled={!canDeletePlayer}
          >
            <Button icon={<DeleteOutlined />} shape="circle" danger ghost disabled={!canDeletePlayer}/>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <SelectedItemsControl
        selected={selectedRows}
        validation={validationStatus}
        clearSelection={() => setSelectedRowKeys([])}
        handleSendWelcomeEmail={handleSendWelcomeEmail}
        canDeletePlayer={canDeletePlayer}
        canSendWelcomeEmail={canSendWelcomeEmail}
        canEditPlayer={canEditPlayer}
      />
      <Table
        rowClassName={(row) => (row.valid ? "" : "invalid-row")}
        columns={columns}
        dataSource={playersWithValidity}
        bordered
        pagination={{ pageSize: 100 }}
        rowKey={(record) => record.player_id}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
        }}
        size="small"
      />
    </div>
  );
};
